import React from 'react';
import './truncatedText.scss';

/** Default max width for part # display; keeps table columns from blowing out. */
export const PART_NUMBER_MAX_WIDTH = 160;

/** Default max width for part description display; keeps price on-screen. */
export const PART_DESCRIPTION_MAX_WIDTH = 320;

/** Narrow-viewport description cap (≤900px wide). */
export const PART_DESCRIPTION_MAX_WIDTH_NARROW = 160;

/** CSS media: description uses narrow max at this width and below. */
export const PART_DESCRIPTION_NARROW_BREAKPOINT = 900;

/**
 * MUI sx value for maxWidth/width (nested under those keys).
 * Narrow through 900px; wide from 901px — MUI `md` alone is min-width 900
 * and would keep 320 at the PR-template 900-wide viewport.
 */
export const partDescriptionMaxWidthSx = {
  xs: PART_DESCRIPTION_MAX_WIDTH_NARROW,
  [`@media (min-width:${PART_DESCRIPTION_NARROW_BREAKPOINT + 1}px)`]: PART_DESCRIPTION_MAX_WIDTH,
};

const ellipsisStyle = {
  display: 'inline-block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'bottom',
};

/** Whole-string http(s) URL — cheapest linkify for catalog / part displays. */
export function isHttpUrl(value) {
  const s = value == null ? '' : String(value).trim();
  return /^https?:\/\/\S+$/i.test(s);
}

/**
 * Read-only text with ellipsis; full value on hover via title.
 * When the entire value is an http(s) URL, render a link (new tab).
 */
export const TruncatedText = ({
  value, maxWidth, className, style, ...rest
}) => {
  const text = value == null || value === '' ? '' : String(value);
  if (!text) return null;
  const mergedStyle = {
    ...ellipsisStyle,
    ...(maxWidth != null ? { maxWidth } : {}),
    ...style,
  };
  if (isHttpUrl(text)) {
    const href = text.trim();
    // Global `a { color: #FFF }` (style-helpers) is for dark chrome; catalog
    // cells are white — force a readable link color via .truncated-text-link.
    const linkClass = ['truncated-text-link', className].filter(Boolean).join(' ');
    return (
      <a
        className={linkClass}
        href={href}
        target="_new"
        rel="noopener noreferrer"
        title={text}
        style={mergedStyle}
        onClick={(e) => e.stopPropagation()}
        {...rest}
      >
        {text}
      </a>
    );
  }
  return (
    <span
      className={className}
      title={text}
      style={mergedStyle}
      {...rest}
    >
      {text}
    </span>
  );
};

/** Part number alias — default max width 160. */
const TruncatedPartNumber = ({
  value, maxWidth = PART_NUMBER_MAX_WIDTH, className, style, ...rest
}) => (
  <TruncatedText
    value={value}
    maxWidth={maxWidth}
    className={className}
    style={style}
    {...rest}
  />
);

/**
 * Part description alias — responsive max width via `.truncated-part-description`
 * unless `maxWidth` is passed explicitly.
 */
export const TruncatedPartDescription = ({
  value, maxWidth, className, style, ...rest
}) => {
  const responsive = maxWidth == null;
  const classes = [responsive ? 'truncated-part-description' : null, className]
    .filter(Boolean)
    .join(' ') || undefined;
  return (
    <TruncatedText
      value={value}
      maxWidth={responsive ? undefined : maxWidth}
      className={classes}
      style={style}
      {...rest}
    />
  );
};

/** MUI TextField sx: ellipsis when value overflows the field width. */
export const truncatedTextInputSx = {
  maxWidth: '100%',
  '& .MuiInputBase-input': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

export const truncatedPartNumberInputSx = truncatedTextInputSx;
export const truncatedPartDescriptionInputSx = truncatedTextInputSx;

/** Inline style for bootstrap form-control part number inputs. */
export const truncatedPartNumberInputStyle = {
  maxWidth: PART_NUMBER_MAX_WIDTH,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** Inline style for constrained description inputs (desktop default). */
export const truncatedPartDescriptionInputStyle = {
  maxWidth: PART_DESCRIPTION_MAX_WIDTH,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export default TruncatedPartNumber;
